import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Lottie from 'react-lottie';
import animationData from '../lotties/doneNode_fr.json';
import { TextField, Box, Alert, CircularProgress, Typography, Tooltip, IconButton, FormControlLabel, Checkbox } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ModalLarge from './Base/ModalLarge';
import nodeAxiosFirebase from '../utils/nodeAxiosFirebase';
import Select from '../stories/general-components/Select';
import * as Icons from '@mui/icons-material';
import IconSelector from '../components/@generalComponents/IconSelector';
import { fetchBusinessData } from '../redux/actions-v2/coreAction';

const ModalPulseCreation = ({
  isOpen,
  modalCloseHandler,
  structureId,
  type,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isAnimationPlaying, setIsAnimationPlaying] = useState(true);

  useEffect(() => {
    return () => {
      setIsAnimationPlaying(false);
    };
  }, []);

  const currentLangCode = i18n.language;

  const [title, setTitle] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [structureIdSelected, setStructureIdSelected] = useState(null)
  const [isOpenIcon, setIsOpenIcon] = useState(false);
  const [icon, setIcon] = useState('');
  const [selectedGroups, setSelectedGroups] = useState([]);


  const defaultOptions = {
    loop: false,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
      progressiveLoad: false,
    },
  };

  const currentPlatform = useSelector(state => state.core.currentPlatform);
  const businessPreference = useSelector((state) => state.core.businessData);
  const businessStructure = useSelector(
    (state) => state.core.businessStructure
  );
  const businessStructures = businessStructure?.structures;
  const structure = businessStructures?.find((s) => s.id === structureId);

  const IconStructure = Icons[icon || 'HelpOutline'];

  const groupsList = businessPreference?.groups?.map((group) => group?.identifiant);

  const handleGroupToggle = (group) => {
    setSelectedGroups((prevSelected) =>
      prevSelected.includes(group)
        ? prevSelected.filter((g) => g !== group)
        : [...prevSelected, group]
    );
  };


  const handleTitleChange = (event) => {
    setTitle(event.target.value);
    if (error) {
      setError(null);
    }
  };

  const handleCloseIcon = (iconName) => {
    setIsOpenIcon(false);
    setIcon(iconName);
  };


  const handleCloseEmpty = () => {
    setIsOpenIcon(false);
  };

  const handleCreateBoard = async () => {
    if (!title.trim()) {
      setError(t('validation.titleRequired'));
      return;
    }
    if (!type) {
      setError(t('errors.internalTypeError'));
      console.error("ModalPulseCreation requires a 'type' prop ('dashboard' or 'report').");
      return;
    }

    setIsLoading(true);
    setError(null);
    setIsSuccess(false);

    try {
      const body = {
        title: title.trim(),
        type: type,
        blocks: [],
        structureId: structureIdSelected,
        icon: icon,
        lang: currentLangCode?.slice(0, 2) || 'fr',
        groups: selectedGroups,
      };

      const response = await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: `pulse/manage-board`,
        body: body,
      });

      if (response && response.id) {
        const newBoardId = response.id;
        setIsSuccess(true);

        setTimeout(() => {
          modalCloseHandler();
          navigate(`/pulse/${structureIdSelected}/${newBoardId}`);
          dispatch(fetchBusinessData(businessPreference?.id, t, currentLangCode, null, currentPlatform))
          setTimeout(() => {
            setTitle('');
            setIsSuccess(false);
            setIsLoading(false);
          }, 300);
        }, 1800);

      } else {
        throw new Error(t('errors.invalidApiResponse'));
      }

    } catch (err) {
      console.error(`Error creating ${type}:`, err);
      setError(err.message || t('errors.genericError'));
      setIsLoading(false);
    }
  };

  const typeDisplayName = type === 'dashboard' ? t('dashboard') : t('report');

  return (
    <ModalLarge
      isOpen={isOpen}
      subTitle={t('modalPulse.subTitle', { type: typeDisplayName })}
      modalCloseHandler={!isLoading ? modalCloseHandler : () => { }}
      icon={structure?.icon}
      title={t('modalPulseTitle', { type: typeDisplayName, structureName: structure?.name || '' })}
      labels={{
        primary: t('create')
      }}
      handlePrim={handleCreateBoard}
      isActionDisabled={isLoading || isSuccess || !title.trim()}
    >
      {isOpenIcon && (
        <IconSelector
          handleCloseIcon={handleCloseIcon}
          isOpen={isOpenIcon}
          handleClose={handleCloseEmpty}
        />
      )}
      <Box sx={{ p: 0, minHeight: '150px' }}>
        {isSuccess ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
            {isAnimationPlaying && (
              <Lottie
                options={defaultOptions}
                height={120}
                width={120}
                isStopped={!isAnimationPlaying}
                isPaused={!isAnimationPlaying}
              />
            )}
            <Typography variant="h6" sx={{ mt: 2 }}>
              {t('modalPulse.success', { type: typeDisplayName })}
            </Typography>
          </Box>
        ) : (
          <Box>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <div className='row'> <div className='col-11'><TextField
              autoFocus
              required
              margin="dense"
              id="boardTitle"
              label={t('pulseTitleLabel', { type: typeDisplayName })}
              type="text"
              fullWidth
              variant="outlined"
              value={title}
              onChange={handleTitleChange}
              disabled={isLoading}
            /></div><div className='col-1 mt-1 align-c'>
                <Tooltip title={t('updateIcon')}>
                  <IconButton onClick={() => setIsOpenIcon(true)} size="large">
                    <IconStructure />
                  </IconButton>
                </Tooltip>

              </div>

              <div className='col-6'>
                <Select
                  label={t('structure')}
                  value={structureIdSelected}
                  onChange={(e, value) => {
                    setStructureIdSelected(value);
                  }}
                  selections={
                    businessStructures?.map((structure) => ({
                      label: structure?.name,
                      value: structure?.id,
                      id: structure?.id,
                    })) || []
                  }
                />
              </div>
              <div className="col-6 py-4">
                {groupsList?.map((group) => (
                  <FormControlLabel
                    key={group}
                    control={
                      <Checkbox
                        checked={selectedGroups?.includes(group)}
                        onChange={() => handleGroupToggle(group)}
                      />
                    }
                    label={businessPreference?.groups?.find(
                      (g) => g?.identifiant === group
                    )?.name}
                  />
                ))}
              </div>
            </div>
            {isLoading && (
              <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </ModalLarge>
  );
};

export default ModalPulseCreation;